import { api } from '@invisoutlet/shared/api';
import { useAuthContext } from '@invisoutlet/shared/context';
import { useAllDevices, useWebSocketStatus } from '@invisoutlet/shared/hooks';
import type { Building, Device, DeviceSensorPayload, Entity } from '@invisoutlet/shared/types';
import {
  aggregateSensors,
  formatRelativeTime,
  generateMockSensorData,
  type SensorAggregation,
} from '@invisoutlet/shared/utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EnterpriseLayout } from '../components/enterprise-layout.js';
import { GroupByBar, type GroupField } from '../components/group-by-bar.js';

export function resolveLevelName(device: Device, building: Building | undefined): string {
  return device.levelEnum
    ? (building?.levelEnum[device.levelEnum] ?? device.levelEnum)
    : '(Default)';
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviceRow {
  device: Device;
  sensor: DeviceSensorPayload | null;
  buildingName: string;
  levelName: string;
  country: string;
  state: string;
  city: string;
}

interface GroupNode {
  path: string;
  label: string;
  depth: number;
  field: GroupField;
  count: number;
  allRows: DeviceRow[];
  children: GroupNode[];
  rows: DeviceRow[];
}

const STATUS_DOT: Record<DeviceSensorPayload['deviceStatus'], string> = {
  online: 'bg-success',
  offline: 'bg-danger',
  error: 'bg-warning',
};

const FIELD_LABELS: Record<GroupField, string> = {
  country: 'Country',
  state: 'State',
  city: 'City',
  buildingName: 'Building',
  levelName: 'Level',
  room: 'Room',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg-primary">
      <div className="divide-y divide-border">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
          <div key={n} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-32 animate-pulse rounded bg-bg-elevated" />
            <div className="h-4 w-24 animate-pulse rounded bg-bg-elevated" />
            <div className="h-4 w-16 animate-pulse rounded bg-bg-elevated" />
            <div className="h-4 flex-1 animate-pulse rounded bg-bg-elevated" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-elevated">
        <svg
          className="h-8 w-8 text-text-secondary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18" />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-text-primary">
        {query ? 'No matching devices' : 'No devices found'}
      </h2>
      <p className="max-w-sm text-sm text-text-secondary">
        {query
          ? 'Try adjusting your search or filters.'
          : 'Devices will appear here once they have been provisioned.'}
      </p>
    </div>
  );
}

// ─── Grouping utilities ───────────────────────────────────────────────────────

/** Returns the string value of a DeviceRow for a given GroupField. */
function getGroupValue(row: DeviceRow, field: GroupField): string {
  switch (field) {
    case 'country':
      return row.country || '(No Country)';
    case 'state':
      return row.state || '(No State)';
    case 'city':
      return row.city || '(No City)';
    case 'buildingName':
      return row.buildingName || '(No Building)';
    case 'levelName':
      return row.levelName || '(No Level)';
    case 'room':
      return row.device.room || '(No Room)';
  }
}

function buildGroupTree(
  rows: DeviceRow[],
  fields: GroupField[],
  depth = 0,
  parentPath = '',
): GroupNode[] {
  if (fields.length === 0) return [];
  const [field, ...rest] = fields;
  const buckets = new Map<string, DeviceRow[]>();
  for (const row of rows) {
    const val = getGroupValue(row, field);
    if (!buckets.has(val)) buckets.set(val, []);
    buckets.get(val)!.push(row);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, groupRows]) => {
      const path = parentPath ? `${parentPath}::${label}` : label;
      return {
        path,
        label,
        depth,
        field,
        count: groupRows.length,
        allRows: groupRows,
        children: rest.length > 0 ? buildGroupTree(groupRows, rest, depth + 1, path) : [],
        rows: rest.length === 0 ? groupRows : [],
      };
    });
}

// ─── Extracted Components ─────────────────────────────────────────────────────

interface LeafRowProps {
  row: DeviceRow;
  temperatureUnit: '°F' | '°C';
}

function LeafRow({ row, temperatureUnit }: LeafRowProps) {
  const { device, sensor, buildingName, levelName } = row;
  return (
    <tr className="transition-colors hover:bg-bg-surface">
      <td className="px-4 py-3 font-medium text-text-primary">{device.deviceName ?? '—'}</td>
      <td className="px-4 py-3 text-text-secondary">{buildingName || '—'}</td>
      <td className="px-4 py-3 text-text-secondary">{levelName || '—'}</td>
      <td className="px-4 py-3 text-text-secondary">{device.room || '—'}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className={`h-2 w-2 rounded-full ${STATUS_DOT[sensor?.deviceStatus ?? 'offline']}`}
            aria-hidden="true"
          />
          <span className="capitalize text-text-secondary">{sensor?.deviceStatus ?? '—'}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-text-primary">
        {sensor == null || sensor.temperature == null
          ? '—'
          : temperatureUnit === '°F'
            ? `${Math.round((sensor.temperature * 9) / 5 + 32)}°F`
            : `${Math.round(sensor.temperature)}°C`}
      </td>
      <td className="px-4 py-3 text-text-primary">
        {sensor == null || sensor.humidity == null ? '—' : `${sensor.humidity}%`}
      </td>
      <td className="px-4 py-3 text-text-secondary">{sensor?.lux ?? '—'}</td>
      <td className="px-4 py-3">
        {sensor == null ? (
          <span className="text-text-secondary">—</span>
        ) : (
          <span
            className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
              sensor.occupancy ? 'bg-success/15 text-success' : 'bg-bg-elevated text-text-secondary'
            }`}
          >
            {sensor.occupancy ? 'Yes' : 'No'}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {sensor == null ? (
          <div className="flex gap-1">
            <span className="text-text-secondary">—</span>
            <span className="text-text-secondary">—</span>
          </div>
        ) : (
          <div className="flex gap-1">
            {(
              [
                { key: 'outlet1', label: 'Outlet 1', on: sensor.outlet1 },
                { key: 'outlet2', label: 'Outlet 2', on: sensor.outlet2 },
              ] as const
            ).map(({ key, label, on }) => (
              <span
                key={key}
                className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
                  on ? 'bg-success/15 text-success' : 'bg-bg-elevated text-text-secondary'
                }`}
              >
                <span className="sr-only">
                  {label}: {on ? 'on' : 'off'}
                </span>
                <span aria-hidden="true">{on ? 'ON' : 'OFF'}</span>
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {sensor?.lastUpdated ? formatRelativeTime(sensor.lastUpdated) : '—'}
      </td>
    </tr>
  );
}

interface GroupRowProps {
  node: GroupNode;
  temperatureUnit: '°F' | '°C';
  isExpanded: boolean;
  onToggle: (path: string) => void;
}

function GroupRow({ node, temperatureUnit, isExpanded, onToggle }: GroupRowProps) {
  const agg: SensorAggregation = aggregateSensors(
    node.allRows.map((r) => r.sensor),
    temperatureUnit,
  );
  const indent = node.depth * 20 + 12;
  const rowStyle = depthStyle(node.depth);

  return (
    <tr className={`${rowStyle} transition-colors hover:brightness-95`}>
      {/* Device col — chevron + label + count */}
      <td className="px-4 py-2.5" style={{ paddingLeft: indent }}>
        <button
          type="button"
          className="flex items-center gap-2 focus:outline-none w-full text-left"
          onClick={() => onToggle(node.path)}
          aria-expanded={isExpanded}
          aria-label={`${FIELD_LABELS[node.field]}: ${node.label}, ${node.count} device${node.count !== 1 ? 's' : ''}`}
        >
          <svg
            className={`h-3.5 w-3.5 shrink-0 text-text-secondary transition-transform duration-150 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <span className="text-xs uppercase tracking-wide text-text-secondary">
            {FIELD_LABELS[node.field]}:
          </span>
          <span className="font-medium">{node.label}</span>
          <span className="rounded-full bg-bg-elevated px-1.5 py-0.5 text-xs text-text-secondary">
            {node.count}
          </span>
        </button>
      </td>

      <td className="px-4 py-2.5 text-xs text-text-secondary">
        {node.field === 'buildingName' ? node.label : '—'}
      </td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">
        {node.field === 'levelName' ? node.label : '—'}
      </td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">
        {node.field === 'room' ? node.label : '—'}
      </td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.statusDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.tempDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.humidityDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.luxDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.occupancyDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.outletsDisplay}</td>
      <td className="px-4 py-2.5 text-xs text-text-secondary">{agg.lastUpdatedDisplay}</td>
    </tr>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type SortKey =
  | 'deviceName'
  | 'buildingName'
  | 'levelName'
  | 'room'
  | 'status'
  | 'temperature'
  | 'humidity'
  | 'lastUpdated';

const GROUP_DEPTH_STYLES = [
  'bg-bg-elevated font-semibold text-text-primary',
  'bg-bg-surface font-medium text-text-primary',
  'bg-bg-primary font-normal text-text-secondary border-t border-border',
];

function depthStyle(depth: number): string {
  return GROUP_DEPTH_STYLES[Math.min(depth, GROUP_DEPTH_STYLES.length - 1)];
}

export default function TableViewPage() {
  const { user } = useAuthContext();

  const entityId = user!.entityId!;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterBuildingId, setFilterBuildingId] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('deviceName');
  const [sortAsc, setSortAsc] = useState(true);
  const [groupByFields, setGroupByFields] = useState<GroupField[]>([]);
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());

  // Reset expand state when grouping config changes
  const prevGroupByRef = useRef<GroupField[]>([]);
  useEffect(() => {
    const prev = prevGroupByRef.current;
    if (prev.length !== groupByFields.length || prev.some((f, i) => f !== groupByFields[i])) {
      setCollapsedPaths(new Set());
      prevGroupByRef.current = groupByFields;
    }
  }, [groupByFields]);

  // Entity (for building/level names)
  const { data: entityRes } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;
  const buildings = entity?.buildings ?? [];

  // Devices
  const { data: devicesRes, isLoading } = useQuery({
    queryKey: ['devices', entityId],
    queryFn: () => api.get<Device[]>(`/api/entities/${entityId}/devices`),
    enabled: !!entityId,
  });

  // Live sensor data from WebSocket store (empty map when not connected)
  const liveDevices = useAllDevices();
  const { connected } = useWebSocketStatus();

  const temperatureUnit = entity?.preference.temperatureUnit ?? '°F';

  // Build enriched rows
  const rows = useMemo((): DeviceRow[] => {
    const devices = devicesRes?.data ?? [];
    return devices
      .filter((device) => device.deviceId)
      .map((device) => {
        const building = entity?.buildings.find((b) => b.id === device.building);
        return {
          device,
          sensor:
            liveDevices[device.deviceId] ??
            (connected ? null : generateMockSensorData(device.deviceId)),
          buildingName: building?.name ?? device.building ?? '',
          levelName: resolveLevelName(device, building),
          country: building?.address.country ?? '',
          state: building?.address.state ?? '',
          city: building?.address.city ?? '',
        };
      });
  }, [devicesRes, entity, liveDevices, connected]);

  // Filter
  const filtered = useMemo(() => {
    let result = rows;
    if (filterBuildingId) {
      result = result.filter((r) => r.device.building === filterBuildingId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          (r.device.deviceName ?? '').toLowerCase().includes(q) ||
          (r.device.room ?? '').toLowerCase().includes(q) ||
          (r.buildingName ?? '').toLowerCase().includes(q) ||
          (r.levelName ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [rows, filterBuildingId, searchQuery]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'deviceName':
          cmp = (a.device.deviceName ?? '').localeCompare(b.device.deviceName ?? '');
          break;
        case 'buildingName':
          cmp = (a.buildingName ?? '').localeCompare(b.buildingName ?? '');
          break;
        case 'levelName':
          cmp = (a.levelName ?? '').localeCompare(b.levelName ?? '');
          break;
        case 'room':
          cmp = (a.device.room ?? '').localeCompare(b.device.room ?? '');
          break;
        case 'status':
          cmp = (a.sensor?.deviceStatus ?? '').localeCompare(b.sensor?.deviceStatus ?? '');
          break;
        case 'temperature':
          cmp = (a.sensor?.temperature ?? -Infinity) - (b.sensor?.temperature ?? -Infinity);
          break;
        case 'humidity':
          cmp = (a.sensor?.humidity ?? -Infinity) - (b.sensor?.humidity ?? -Infinity);
          break;
        case 'lastUpdated':
          cmp =
            new Date(b.sensor?.lastUpdated ?? 0).getTime() -
            new Date(a.sensor?.lastUpdated ?? 0).getTime();
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortAsc]);

  // Group tree (only computed when grouping is active)
  const groupTree = useMemo(
    () => (groupByFields.length > 0 ? buildGroupTree(sorted, groupByFields) : []),
    [sorted, groupByFields],
  );

  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortAsc((prev) => !prev);
      } else {
        setSortKey(key);
        setSortAsc(true);
      }
    },
    [sortKey],
  );

  function toggleGroup(path: string) {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  function exportCsv() {
    const headers = [
      'Device',
      'Building',
      'Floor',
      'Room',
      'Status',
      'Temp',
      'Humidity',
      'Lux',
      'Occupied',
      'Outlet 1',
      'Outlet 2',
      'Last Updated',
    ];
    const rowData = sorted.map((r) => [
      r.device.deviceName,
      r.buildingName,
      r.levelName,
      r.device.room,
      r.sensor?.deviceStatus ?? '',
      r.sensor?.temperature ?? '',
      r.sensor?.humidity ?? '',
      r.sensor?.lux ?? '',
      r.sensor?.occupancy != null ? (r.sensor.occupancy ? 'Yes' : 'No') : '',
      r.sensor?.outlet1 != null ? (r.sensor.outlet1 ? 'ON' : 'OFF') : '',
      r.sensor?.outlet2 != null ? (r.sensor.outlet2 ? 'ON' : 'OFF') : '',
      r.sensor?.lastUpdated ?? '',
    ]);
    const csv = [headers, ...rowData].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'devices.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Table helpers ───────────────────────────────────────────────────────────

  const th = useCallback(
    (label: string, key: SortKey) => (
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-text-secondary"
        aria-sort={sortKey === key ? (sortAsc ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          className="flex items-center gap-1 hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent rounded select-none"
          onClick={() => handleSort(key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSort(key);
            }
          }}
        >
          {label}
          {sortKey === key && <span aria-hidden="true">{sortAsc ? '↑' : '↓'}</span>}
        </button>
      </th>
    ),
    [sortKey, sortAsc, handleSort],
  );

  function renderGroupNodes(nodes: GroupNode[]): JSX.Element[] {
    return nodes.flatMap((node) => {
      const isExpanded = !collapsedPaths.has(node.path);
      const groupRow = (
        <GroupRow
          key={`group:${node.path}`}
          node={node}
          temperatureUnit={temperatureUnit}
          isExpanded={isExpanded}
          onToggle={toggleGroup}
        />
      );

      if (!isExpanded) return [groupRow];

      const children: JSX.Element[] =
        node.children.length > 0
          ? renderGroupNodes(node.children)
          : node.rows.map((row) => (
              <LeafRow key={row.device.deviceId} row={row} temperatureUnit={temperatureUnit} />
            ));

      return [groupRow, ...children];
    });
  }

  return (
    <EnterpriseLayout title="Table View">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-text-primary">Table View</h1>
            <p className="mt-1 text-sm text-text-secondary">
              All devices across all buildings
              {!isLoading && ` · ${filtered.length} device${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className="rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>

        {/* Toolbar */}
        <div className="mb-3 flex flex-wrap gap-3">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search devices, rooms, buildings…"
            className="w-64 rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Search devices"
          />

          {buildings.length > 1 && (
            <select
              value={filterBuildingId}
              onChange={(e) => setFilterBuildingId(e.target.value)}
              className="rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Filter by building"
            >
              <option value="">All buildings</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Group by bar */}
        <div className="mb-4">
          <GroupByBar active={groupByFields} onChange={setGroupByFields} />
        </div>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton />
        ) : sorted.length === 0 ? (
          <EmptyState query={searchQuery} />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-bg-primary">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-surface">
                  {th('Device', 'deviceName')}
                  {th('Building', 'buildingName')}
                  {th('Floor', 'levelName')}
                  {th('Room', 'room')}
                  {th('Status', 'status')}
                  {th('Temp', 'temperature')}
                  {th('Humidity', 'humidity')}
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-text-secondary"
                  >
                    Lux
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-text-secondary"
                  >
                    Occupied
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-text-secondary"
                  >
                    Outlets
                  </th>
                  {th('Updated', 'lastUpdated')}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {groupByFields.length === 0
                  ? sorted.map((row) => (
                      <LeafRow
                        key={row.device.deviceId}
                        row={row}
                        temperatureUnit={temperatureUnit}
                      />
                    ))
                  : renderGroupNodes(groupTree)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
}
